import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { API } from "../state/AppContext";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Lock, Search, LogOut, Users, Award, CheckCircle2, ExternalLink, Play, Image as ImageIcon, Dices, QrCode, Plus, ChevronLeft, ShieldCheck, Trash2, Crop, Mail, CalendarClock, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { cloudinaryThumb } from "../lib/cloudinary";
import InstagramCropper from "../components/InstagramCropper";

const PASSWORD_KEY = "scl_admin_pwd";

function toCSV(rows) {
  const headers = ["First Name", "Last Name", "Email", "Phone", "Grade", "Contact Consent", "Media Consent", "Points", "Submissions", "Signed Up"];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push([
      escape(r.first_name), escape(r.last_name), escape(r.email), escape(r.phone || ""),
      escape(r.grade), escape(r.consent ? "Yes" : "No"), escape(r.media_consent ? "Yes" : "No"),
      escape(r.points), escape(r.submissions_count), escape(r.created_at),
    ].join(","));
  }
  return lines.join("\n");
}

function downloadFile(filename, content, mime = "text/csv;charset=utf-8") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function AdminScreen() {
  const [password, setPassword] = useState(() => sessionStorage.getItem(PASSWORD_KEY) || "");
  const [adminEmail, setAdminEmail] = useState(() => sessionStorage.getItem("scl_admin_email") || "");
  const [isSuperAdmin, setIsSuperAdmin] = useState(() => sessionStorage.getItem("scl_is_super_admin") === "1");
  const [authed, setAuthed] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [spinStats, setSpinStats] = useState({ total: 0, breakdown: [], recent: [] });
  const [guideLeads, setGuideLeads] = useState({ total: 0, unique_emails: 0, recent: [] });
  const [siteClicks, setSiteClicks] = useState({ total: 0, identified: 0, unique_users: 0, breakdown: [], recent: [] });
  const [tourClicks, setTourClicks] = useState({ week: 0, total: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempting, setAttempting] = useState(false);
  const [tab, setTab] = useState("participants");
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const load = async (pwd) => {
    setLoading(true);
    try {
      const [pRes, sRes, spRes, glRes, scRes, tcRes] = await Promise.all([
        axios.get(`${API}/admin/participants`, { params: { password: pwd } }),
        axios.get(`${API}/admin/submissions`, { params: { password: pwd } }),
        axios.get(`${API}/admin/spin-events`, { params: { password: pwd } }),
        axios.get(`${API}/admin/guide-leads`, { params: { password: pwd } }),
        axios.get(`${API}/admin/site-clicks`, { params: { password: pwd } }),
        axios.get(`${API}/admin/tour-clicks`, { params: { password: pwd } }),
      ]);
      setParticipants(pRes.data);
      setSubmissions(sRes.data);
      setSpinStats(spRes.data);
      setGuideLeads(glRes.data);
      setSiteClicks(scRes.data);
      setTourClicks(tcRes.data);
      setAuthed(true);
      sessionStorage.setItem(PASSWORD_KEY, pwd);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Incorrect password");
        sessionStorage.removeItem(PASSWORD_KEY);
        setAuthed(false);
      } else {
        toast.error("Couldn't load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Emergent Auth callback: /admin#session_id=... — exchange, unlock, clean fragment.
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      if (sid) {
        axios.post(`${API}/auth/emergent/callback`, { session_id: sid })
          .then((r) => {
            if (r.data?.admin_token) {
              // Clear the fragment before we call load() so a reload isn't a re-exchange
              window.history.replaceState(null, "", "/admin");
              // Persist the signed-in email so uploads auto-populate the "Submitted By" column
              if (r.data.email) {
                sessionStorage.setItem("scl_admin_email", r.data.email);
                setAdminEmail(r.data.email);
              }
              const superFlag = !!r.data.is_super_admin;
              sessionStorage.setItem("scl_is_super_admin", superFlag ? "1" : "0");
              setIsSuperAdmin(superFlag);
              toast.success(`Welcome, ${r.data.name || r.data.email}${superFlag ? " (super admin)" : ""}`);
              load(r.data.admin_token);
              return;
            }
            toast.error("Sign-in did not return a token");
          })
          .catch((e) => toast.error(e?.response?.data?.detail || "Sign-in failed"));
        return; // don't fall through to the saved-password path this render
      }
    }
    const saved = sessionStorage.getItem(PASSWORD_KEY);
    if (saved) load(saved);
  }, []);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!password) return;
    setAttempting(true);
    await load(password);
    setAttempting(false);
  };

  const logout = () => {
    sessionStorage.removeItem(PASSWORD_KEY);
    sessionStorage.removeItem("scl_admin_email");
    sessionStorage.removeItem("scl_is_super_admin");
    setPassword("");
    setAdminEmail("");
    setIsSuperAdmin(false);
    setAuthed(false);
    setParticipants([]);
    setSubmissions([]);
  };

  const filteredParticipants = useMemo(() => {
    if (!q.trim()) return participants;
    const s = q.toLowerCase();
    return participants.filter((r) =>
      r.first_name?.toLowerCase().includes(s) ||
      r.last_name?.toLowerCase().includes(s) ||
      r.email?.toLowerCase().includes(s) ||
      (r.phone || "").toLowerCase().includes(s) ||
      (r.grade || "").toLowerCase().includes(s)
    );
  }, [participants, q]);

  const filteredSubmissions = useMemo(() => {
    if (!q.trim()) return submissions;
    const s = q.toLowerCase();
    return submissions.filter((r) =>
      r.user_name?.toLowerCase().includes(s) ||
      r.user_email?.toLowerCase().includes(s) ||
      r.challenge_title?.toLowerCase().includes(s) ||
      (r.caption || "").toLowerCase().includes(s)
    );
  }, [submissions, q]);

  const totals = {
    participants: participants.length,
    submissions: submissions.length,
    points: participants.reduce((a, r) => a + (r.points || 0), 0),
    optIns: participants.filter((r) => r.consent).length,
  };

  const deleteParticipant = async (r) => {
    if (!window.confirm(`Delete participant ${r.first_name} ${r.last_name} (${r.email})? Their submissions will be hidden.`)) return;
    try {
      await axios.delete(`${API}/admin/participants/${r.id}`, { params: { password, admin_email: adminEmail } });
      toast.success("Participant deleted");
      load(password);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const deleteSubmission = async (s) => {
    if (!window.confirm(`Delete submission "${s.challenge_title}" by ${s.user_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/submissions/${s.id}`, { params: { password, admin_email: adminEmail } });
      toast.success("Submission removed");
      load(password);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleSelect = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAllVisible = () => setSelectedIds((s) => {
    const visibleIds = filteredParticipants.map((r) => r.id);
    const allSelected = visibleIds.every((id) => s.has(id));
    if (allSelected) {
      const n = new Set(s);
      visibleIds.forEach((id) => n.delete(id));
      return n;
    }
    const n = new Set(s);
    visibleIds.forEach((id) => n.add(id));
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());

  const bulkDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Permanently delete ${ids.length} participant(s)? Their submissions will be hidden.`)) return;
    try {
      const r = await axios.post(`${API}/admin/participants/bulk-delete`, { ids }, { params: { password, admin_email: adminEmail } });
      toast.success(`Deleted ${r.data.deleted || ids.length} participant(s)`);
      clearSelection();
      load(password);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    }
  };

  const bulkExportSelected = () => {
    const set = selectedIds;
    const rows = participants.filter((r) => set.has(r.id));
    if (rows.length === 0) { toast.error("No selection to export"); return; }
    const csv = toCSV(rows);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadFile(`scl-kindness-selected-${rows.length}-${stamp}.csv`, csv);
    toast.success(`Exported ${rows.length} selected row(s)`);
  };

  const exportCSV = (onlyOptIns = false) => {
    const source = onlyOptIns ? filteredParticipants.filter((r) => r.consent) : filteredParticipants;
    if (source.length === 0) {
      toast.error(onlyOptIns ? "No opt-in participants in current filter" : "No participants to export");
      return;
    }
    const csv = toCSV(source);
    const stamp = new Date().toISOString().slice(0, 10);
    const suffix = onlyOptIns ? "optins" : "all";
    downloadFile(`scl-kindness-${suffix}-${stamp}.csv`, csv);
    toast.success(`Exported ${source.length} ${onlyOptIns ? "opt-in " : ""}rows`);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-scl-cream flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={submitPassword} className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-warm">
          <div className="w-14 h-14 rounded-full bg-scl-teal/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-scl-teal" />
          </div>
          <h1 className="font-serif text-2xl text-center text-scl-charcoal font-bold mb-1">Admin access</h1>
          <p className="text-sm text-scl-charcoal/60 text-center mb-6">SCL Kindness Challenge dashboard</p>
          <label className="text-xs uppercase tracking-widest text-scl-charcoal/60 font-semibold">Password</label>
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 h-12 rounded-xl border-scl-border bg-white"
            placeholder="Enter admin password"
            autoFocus
          />
          <button
            type="submit"
            disabled={attempting || !password}
            data-testid="admin-login-btn"
            className="mt-5 w-full h-12 rounded-full bg-scl-coral text-white font-semibold shadow-warm-sm hover:brightness-105 active:scale-[0.98] transition-all disabled:opacity-70"
          >
            {attempting ? "Checking…" : "Sign in"}
          </button>

          <GoogleSignInScaffold onAuth={(pwd) => load(pwd)} />

          <a href="/" className="mt-4 block text-center text-xs text-scl-charcoal/60 hover:text-scl-teal underline underline-offset-4">Back to app</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-scl-cream" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_scl-kindness/artifacts/6382qm3g_SCL-final-logo-clearspace-namelogo-cropped%20%282%29.png"
              alt="SCL"
              className="h-10 w-auto"
            />
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-scl-teal font-semibold">Admin dashboard</div>
              <div className="font-serif text-lg text-scl-charcoal font-bold">Kindness Challenge</div>
              {adminEmail && (
                <div className="mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid="admin-identity">
                  <span className="text-[11px] text-scl-charcoal/60">{adminEmail}</span>
                  {isSuperAdmin && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-scl-brick text-white text-[9px] font-bold uppercase tracking-widest" data-testid="admin-super-badge">
                      <ShieldCheck className="w-3 h-3" /> Super admin
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => load(password)} className="h-10 px-4 rounded-full bg-white border border-scl-border text-sm font-medium hover:bg-scl-cream" data-testid="admin-refresh">Refresh</button>
            <button onClick={() => exportCSV(true)} disabled={loading} className="h-10 px-4 rounded-full bg-scl-mustard text-scl-charcoal text-sm font-semibold flex items-center gap-2 hover:brightness-105 active:scale-95 disabled:opacity-60" data-testid="admin-export-optins">
              <Download className="w-4 h-4" /> Export opt-ins only
            </button>
            <button onClick={() => exportCSV(false)} disabled={loading || participants.length === 0} className="h-10 px-4 rounded-full bg-scl-teal text-white text-sm font-semibold flex items-center gap-2 hover:brightness-110 active:scale-95 disabled:opacity-60" data-testid="admin-export-all">
              <Download className="w-4 h-4" /> Export all
            </button>
            <button onClick={logout} className="h-10 w-10 rounded-full bg-white border border-scl-border flex items-center justify-center hover:bg-scl-cream" data-testid="admin-logout" title="Sign out">
              <LogOut className="w-4 h-4 text-scl-charcoal/70" />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="admin-stats">
          <StatCard icon={Users} label="Participants" value={totals.participants} color="text-scl-teal" bg="bg-scl-teal/10" />
          <StatCard icon={Award} label="Submissions" value={totals.submissions} color="text-scl-coral" bg="bg-scl-coral/10" />
          <StatCard icon={Award} label="Total points" value={totals.points} color="text-scl-brick" bg="bg-scl-brick/10" />
          <StatCard icon={CheckCircle2} label="Marketing opt-ins" value={totals.optIns} color="text-scl-charcoal" bg="bg-scl-mustard/40" />
        </div>

        {/* Tour click tracker — this-week momentum for the Book-a-Tour CTA */}
        <div className="bg-white rounded-2xl shadow-warm-sm p-4 mb-6 flex items-center gap-4 flex-wrap" data-testid="tour-clicks-tile">
          <div className="w-12 h-12 rounded-2xl bg-scl-teal/10 flex items-center justify-center shrink-0"><CalendarClock className="w-6 h-6 text-scl-teal" /></div>
          <div className="flex-1 min-w-[180px]">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-scl-teal">Book a Tour clicks</div>
            <div className="text-scl-charcoal font-serif text-2xl font-bold leading-none mt-0.5" data-testid="tour-clicks-week">{tourClicks.week}</div>
            <div className="text-xs text-scl-charcoal/60 mt-0.5">this week · <span className="font-semibold text-scl-charcoal/70" data-testid="tour-clicks-total">{tourClicks.total}</span> all-time</div>
          </div>
          {(tourClicks.intents_week > 0 || tourClicks.intents_open > 0) && (
            <div className="text-right pr-1" data-testid="tour-clicks-intents">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-scl-brick">Warm intents</div>
              <div className="font-serif text-2xl font-bold leading-none mt-0.5 text-scl-brick">{tourClicks.intents_week || 0}</div>
              <div className="text-xs text-scl-charcoal/60 mt-0.5">this week{tourClicks.intents_open > 0 ? ` · ${tourClicks.intents_open} open` : ""}</div>
            </div>
          )}
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tour-clicks-open"
            className="h-9 px-3 rounded-full border border-scl-teal text-scl-teal text-xs font-semibold inline-flex items-center gap-1 hover:bg-scl-teal/10"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Preview home
          </a>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-scl-charcoal/40" />
          <Input
            data-testid="admin-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tab === "participants" ? "Search by name, email, phone, grade…" : "Search by name, challenge, caption…"}
            className="pl-9 h-11 rounded-xl border-scl-border bg-white"
          />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 h-11 bg-white rounded-full p-1 shadow-warm-sm border border-scl-border">
            <TabsTrigger value="participants" data-testid="tab-participants" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
              Participants
            </TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
              Submissions
            </TabsTrigger>
            <TabsTrigger value="spins" data-testid="tab-spins" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
              Spinner Activity
            </TabsTrigger>
            <TabsTrigger value="guide" data-testid="tab-guide" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
              Guide Leads
            </TabsTrigger>
            <TabsTrigger value="clicks" data-testid="tab-clicks" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
              Site Clicks
            </TabsTrigger>
            <TabsTrigger value="qrcodes" data-testid="tab-qrcodes" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
              Event QR Codes
            </TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="tab-enrollments" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
              Enrollments
            </TabsTrigger>
            <TabsTrigger value="staffuploads" data-testid="tab-staffuploads" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
              Staff Uploads
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="familyinvites" data-testid="tab-familyinvites" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
                Family Invites
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="staffsignins" data-testid="tab-staffsignins" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
                Staff Sign-ins
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="digest" data-testid="tab-digest" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
                Parent Digest
              </TabsTrigger>
            )}
            {isSuperAdmin && (
              <TabsTrigger value="tours" data-testid="tab-tours" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
                Tours
              </TabsTrigger>
            )}
            <TabsTrigger value="eventocr" data-testid="tab-eventocr" className="rounded-full px-5 data-[state=active]:bg-scl-teal data-[state=active]:text-white text-scl-charcoal/70 font-medium">
              Event Sign-ins
            </TabsTrigger>
          </TabsList>

          <TabsContent value="participants">
            {isSuperAdmin && selectedIds.size > 0 && (
              <div className="bg-scl-charcoal text-white rounded-2xl p-3 mb-3 flex items-center justify-between gap-3 flex-wrap shadow-warm" data-testid="bulk-action-bar">
                <div className="text-sm font-semibold">{selectedIds.size} selected</div>
                <div className="flex items-center gap-2">
                  <button onClick={bulkExportSelected} data-testid="bulk-export-btn" className="h-9 px-3 rounded-full bg-scl-mustard text-scl-charcoal text-xs font-bold inline-flex items-center gap-1 hover:brightness-105">
                    <Download className="w-3.5 h-3.5" /> Export selected
                  </button>
                  <button onClick={bulkDeleteSelected} data-testid="bulk-delete-btn" className="h-9 px-3 rounded-full bg-scl-brick text-white text-xs font-bold inline-flex items-center gap-1 hover:brightness-105">
                    <Trash2 className="w-3.5 h-3.5" /> Delete selected
                  </button>
                  <button onClick={clearSelection} data-testid="bulk-clear-btn" className="h-9 px-3 rounded-full border border-white/30 text-xs font-medium hover:bg-white/10">Clear</button>
                </div>
              </div>
            )}
            <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-table">
                  <thead className="bg-scl-cream text-left">
                    <tr>
                      {isSuperAdmin && (
                        <Th className="w-10">
                          <input
                            type="checkbox"
                            data-testid="bulk-select-all"
                            aria-label="Select all visible"
                            checked={filteredParticipants.length > 0 && filteredParticipants.every((r) => selectedIds.has(r.id))}
                            onChange={toggleSelectAllVisible}
                            className="w-4 h-4 accent-scl-brick align-middle"
                          />
                        </Th>
                      )}
                      <Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Grade</Th><Th>Contact</Th><Th>Media</Th>
                      <Th className="text-right">Points</Th><Th className="text-right">Subs</Th><Th>Signed up</Th>{isSuperAdmin && <Th></Th>}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan={isSuperAdmin ? 11 : 9} className="p-6 text-center text-scl-charcoal/60">Loading…</td></tr>}
                    {!loading && filteredParticipants.length === 0 && (
                      <tr><td colSpan={isSuperAdmin ? 11 : 9} className="p-8 text-center text-scl-charcoal/60">No participants{q ? " match your search" : " yet"}.</td></tr>
                    )}
                    {filteredParticipants.map((r) => (
                      <tr key={r.id} className={`border-t border-scl-border/60 ${selectedIds.has(r.id) ? "bg-scl-mustard/10" : "hover:bg-scl-cream/50"}`} data-testid={`admin-row-${r.id}`}>
                        {isSuperAdmin && (
                          <Td>
                            <input
                              type="checkbox"
                              data-testid={`bulk-select-${r.id}`}
                              aria-label={`Select ${r.first_name} ${r.last_name}`}
                              checked={selectedIds.has(r.id)}
                              onChange={() => toggleSelect(r.id)}
                              className="w-4 h-4 accent-scl-brick align-middle"
                            />
                          </Td>
                        )}
                        <Td><span className="font-medium text-scl-charcoal">{r.first_name} {r.last_name}</span></Td>
                        <Td><a href={`mailto:${r.email}`} className="text-scl-teal hover:underline">{r.email}</a></Td>
                        <Td>{r.phone || <span className="text-scl-charcoal/30">—</span>}</Td>
                        <Td>{r.grade}</Td>
                        <Td>{r.consent
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-scl-teal"><CheckCircle2 className="w-3.5 h-3.5" /> Yes</span>
                          : <span className="text-xs text-scl-charcoal/40">No</span>}
                        </Td>
                        <Td>{r.media_consent
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-scl-coral"><CheckCircle2 className="w-3.5 h-3.5" /> Yes</span>
                          : <span className="text-xs text-scl-charcoal/40">No</span>}
                        </Td>
                        <Td className="text-right font-bold">{r.points}</Td>
                        <Td className="text-right">{r.submissions_count}</Td>
                        <Td className="text-scl-charcoal/60 text-xs">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</Td>
                        {isSuperAdmin && (
                          <Td className="text-right">
                            <button onClick={() => deleteParticipant(r)} data-testid={`admin-delete-${r.id}`} title="Delete participant" className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-scl-brick/10 text-scl-brick">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="mt-3 text-xs text-scl-charcoal/50">Showing {filteredParticipants.length} of {participants.length}. Export respects current filter.</p>
          </TabsContent>

          <TabsContent value="submissions">
            {loading && <div className="text-scl-charcoal/60 text-sm">Loading…</div>}
            {!loading && filteredSubmissions.length === 0 && (
              <div className="bg-white rounded-2xl p-8 text-center text-scl-charcoal/60 shadow-warm-sm">No submissions{q ? " match your search" : " yet"}.</div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredSubmissions.map((s) => {
                const isVideo = s.media_type === "video" || s.media_type?.startsWith("video/");
                const isHttp = s.media_url?.startsWith("http");
                const thumb = isHttp ? cloudinaryThumb(s.media_url, { w: 400, h: 400 }) : (s.media_url ? `${API}/files/${s.media_url}` : null);
                return (
                  <div key={s.id} className="bg-white rounded-2xl overflow-hidden shadow-warm-sm" data-testid={`admin-submission-${s.id}`}>
                    <a href={s.media_url || "#"} target="_blank" rel="noreferrer" className="block relative aspect-square bg-scl-cream group">
                      {thumb ? (
                        <img src={thumb} alt="submission" className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-scl-charcoal/40 text-xs"><ImageIcon className="w-8 h-8" /></div>
                      )}
                      {isVideo && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-black/60 flex items-center justify-center">
                            <Play className="w-5 h-5 text-white ml-0.5" />
                          </div>
                        </div>
                      )}
                      {s.media_url && (
                        <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 text-white text-[10px] px-2 py-1 rounded-full flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> Open
                        </div>
                      )}
                    </a>
                    <div className="p-3">
                      <div className="text-[13px] font-semibold text-scl-charcoal leading-snug line-clamp-2 mb-1">{s.challenge_title}</div>
                      <div className="text-xs text-scl-charcoal/60 mb-2">by <span className="font-medium text-scl-charcoal/80">{s.user_name}</span></div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-scl-mustard text-scl-charcoal px-2 py-0.5 rounded-full">+{s.points}</span>
                        <span className="text-[10px] text-scl-charcoal/50">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</span>
                      </div>
                      {s.caption && <div className="text-xs text-scl-charcoal/70 mt-2 italic line-clamp-2">&ldquo;{s.caption}&rdquo;</div>}
                      {isSuperAdmin && (
                        <button onClick={() => deleteSubmission(s)} data-testid={`admin-delete-sub-${s.id}`} className="mt-2 text-[11px] text-scl-brick hover:underline inline-flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-xs text-scl-charcoal/50">Showing {filteredSubmissions.length} of {submissions.length}. Click any tile to open the full photo/video in Cloudinary.</p>
          </TabsContent>

          <TabsContent value="spins" data-testid="spins-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <StatCard icon={Dices} label="Total spins" value={spinStats.total || 0} color="text-scl-teal" bg="bg-scl-teal/10" />
              {spinStats.breakdown.slice(0, 3).map((b, i) => (
                <StatCard
                  key={i}
                  icon={Dices}
                  label={b.utm_content || (b.source_path ? `${b.source_path} (no utm)` : "direct / no utm")}
                  value={b.count}
                  color="text-scl-coral"
                  bg="bg-scl-coral/10"
                />
              ))}
            </div>

            <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden mb-4" data-testid="spin-breakdown">
              <div className="p-4 border-b border-scl-border">
                <div className="font-serif text-lg font-bold text-scl-charcoal">Breakdown by QR source</div>
                <div className="text-xs text-scl-charcoal/60">Grouped by <code className="text-[11px]">utm_content</code> + <code className="text-[11px]">source_path</code></div>
              </div>
              {spinStats.breakdown.length === 0 && (
                <div className="p-6 text-center text-scl-charcoal/60 text-sm">No spins recorded yet.</div>
              )}
              {spinStats.breakdown.length > 0 && (
                <table className="w-full text-sm">
                  <thead className="bg-scl-cream text-left">
                    <tr><Th>utm_content</Th><Th>source_path</Th><Th className="text-right">Spins</Th></tr>
                  </thead>
                  <tbody>
                    {spinStats.breakdown.map((b, i) => (
                      <tr key={i} className="border-t border-scl-border/60">
                        <Td><span className="font-mono text-xs">{b.utm_content || <span className="text-scl-charcoal/40">—</span>}</span></Td>
                        <Td><span className="font-mono text-xs">{b.source_path || <span className="text-scl-charcoal/40">—</span>}</span></Td>
                        <Td className="text-right font-bold">{b.count}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden" data-testid="spin-recent">
              <div className="p-4 border-b border-scl-border">
                <div className="font-serif text-lg font-bold text-scl-charcoal">Recent spins</div>
                <div className="text-xs text-scl-charcoal/60">Latest {spinStats.recent.length} events</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-scl-cream text-left">
                    <tr>
                      <Th>Time</Th><Th>Activity</Th><Th>Lang</Th>
                      <Th>utm_content</Th><Th>source</Th><Th>campaign</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {spinStats.recent.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-scl-charcoal/60">No spins yet.</td></tr>
                    )}
                    {spinStats.recent.map((e) => (
                      <tr key={e.id} className="border-t border-scl-border/60" data-testid={`spin-row-${e.id}`}>
                        <Td className="text-xs text-scl-charcoal/70">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</Td>
                        <Td>{e.activity_name || <span className="text-scl-charcoal/40">—</span>}</Td>
                        <Td><span className="uppercase text-[10px] font-bold">{e.language || "en"}</span></Td>
                        <Td><span className="font-mono text-xs">{e.utm_content || <span className="text-scl-charcoal/40">—</span>}</span></Td>
                        <Td><span className="font-mono text-xs">{e.utm_source || <span className="text-scl-charcoal/40">—</span>}</span></Td>
                        <Td><span className="font-mono text-xs">{e.utm_campaign || <span className="text-scl-charcoal/40">—</span>}</span></Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="guide" data-testid="guide-panel">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
              <StatCard icon={Download} label="Total unlocks" value={guideLeads.total || 0} color="text-scl-teal" bg="bg-scl-teal/10" />
              <StatCard icon={CheckCircle2} label="Unique emails" value={guideLeads.unique_emails || 0} color="text-scl-coral" bg="bg-scl-coral/10" />
              <StatCard icon={Users} label="With phone" value={(guideLeads.recent || []).filter((r) => r.phone).length} color="text-scl-charcoal" bg="bg-scl-mustard/40" />
            </div>

            <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden mb-4 flex items-center justify-between p-4 gap-3">
              <div className="text-xs text-scl-charcoal/70">Export all guide-lead emails as CSV for follow-up.</div>
              <button
                data-testid="export-guide-leads"
                onClick={() => {
                  const rows = guideLeads.recent || [];
                  if (rows.length === 0) { toast.error("No leads yet"); return; }
                  const headers = ["Email","Phone","Language","Source Path","utm_content","utm_source","utm_campaign","Trigger Activity","Unlocked At"];
                  const esc = (v) => { if (v == null) return ""; const s = String(v).replace(/"/g, '""'); return /[",\n]/.test(s) ? `"${s}"` : s; };
                  const csv = [headers.join(",")].concat(rows.map((r) => [
                    esc(r.email), esc(r.phone||""), esc(r.language||""), esc(r.source_path||""),
                    esc(r.utm_content||""), esc(r.utm_source||""), esc(r.utm_campaign||""),
                    esc(r.trigger_activity_name||""), esc(r.created_at||""),
                  ].join(","))).join("\n");
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url;
                  a.download = `scl-guide-leads-${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success(`Exported ${rows.length} leads`);
                }}
                className="h-10 px-4 rounded-full bg-scl-teal text-white text-sm font-semibold flex items-center gap-2 hover:brightness-110"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>

            <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden" data-testid="guide-recent">
              <div className="p-4 border-b border-scl-border">
                <div className="font-serif text-lg font-bold text-scl-charcoal">Recent guide unlocks</div>
                <div className="text-xs text-scl-charcoal/60">Latest {guideLeads.recent.length} unlocks. Same email can appear more than once if they spun again.</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-scl-cream text-left">
                    <tr>
                      <Th>Email</Th><Th>Phone</Th><Th>Lang</Th>
                      <Th>utm_content</Th><Th>Triggered by</Th><Th>Unlocked at</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {guideLeads.recent.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-scl-charcoal/60">No guide unlocks yet.</td></tr>
                    )}
                    {guideLeads.recent.map((r) => (
                      <tr key={r.id} className="border-t border-scl-border/60" data-testid={`guide-row-${r.id}`}>
                        <Td><a href={`mailto:${r.email}`} className="text-scl-teal hover:underline font-medium">{r.email}</a></Td>
                        <Td>{r.phone || <span className="text-scl-charcoal/30">—</span>}</Td>
                        <Td><span className="uppercase text-[10px] font-bold">{r.language || "en"}</span></Td>
                        <Td><span className="font-mono text-xs">{r.utm_content || <span className="text-scl-charcoal/40">—</span>}</span></Td>
                        <Td className="text-xs">{r.trigger_activity_name || <span className="text-scl-charcoal/40">—</span>}</Td>
                        <Td className="text-xs text-scl-charcoal/70">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="clicks" data-testid="clicks-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <StatCard icon={ExternalLink} label="Total clicks" value={siteClicks.total || 0} color="text-scl-teal" bg="bg-scl-teal/10" />
              <StatCard icon={Users} label="Identified clicks" value={siteClicks.identified || 0} color="text-scl-coral" bg="bg-scl-coral/10" />
              <StatCard icon={CheckCircle2} label="Unique users" value={siteClicks.unique_users || 0} color="text-scl-charcoal" bg="bg-scl-mustard/40" />
              <StatCard icon={Award} label="Anonymous" value={(siteClicks.total || 0) - (siteClicks.identified || 0)} color="text-scl-charcoal/70" bg="bg-scl-cream" />
            </div>

            {siteClicks.breakdown?.length > 0 && (
              <div className="bg-white rounded-2xl shadow-warm-sm p-4 mb-4">
                <div className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold mb-2">By link type</div>
                <div className="flex flex-wrap gap-2">
                  {siteClicks.breakdown.map((b) => (
                    <span key={b.link_type} className="px-3 py-1 rounded-full bg-scl-teal/10 text-scl-teal text-xs font-semibold" data-testid={`clicks-bd-${b.link_type}`}>
                      {b.link_type} · {b.count}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden mb-4 flex items-center justify-between p-4 gap-3">
              <div className="text-xs text-scl-charcoal/70">Export outbound-click log (who clicked through to schoolforcommunitylearning.org).</div>
              <button
                data-testid="export-site-clicks"
                onClick={() => {
                  const rows = siteClicks.recent || [];
                  if (rows.length === 0) { toast.error("No clicks yet"); return; }
                  const headers = ["First Name","Last Name","Email","Phone","Grade","Contact Consent","Link Type","Destination","Source Path","Language","utm_content","utm_source","utm_campaign","utm_medium","Clicked At"];
                  const esc = (v) => { if (v == null) return ""; const s = String(v).replace(/"/g, '""'); return /[",\n]/.test(s) ? `"${s}"` : s; };
                  const csv = [headers.join(",")].concat(rows.map((r) => [
                    esc(r.first_name||""), esc(r.last_name||""), esc(r.email||""), esc(r.phone||""), esc(r.grade||""),
                    esc(r.contact_consent ? "yes" : ""), esc(r.link_type||""), esc(r.destination_url||""),
                    esc(r.source_path||""), esc(r.language||""),
                    esc(r.utm_content||""), esc(r.utm_source||""), esc(r.utm_campaign||""), esc(r.utm_medium||""),
                    esc(r.created_at||""),
                  ].join(","))).join("\n");
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url;
                  a.download = `scl-site-clicks-${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success(`Exported ${rows.length} clicks`);
                }}
                className="h-10 px-4 rounded-full bg-scl-teal text-white text-sm font-semibold flex items-center gap-2 hover:brightness-110"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>

            <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden" data-testid="clicks-recent">
              <div className="p-4 border-b border-scl-border">
                <div className="font-serif text-lg font-bold text-scl-charcoal">Recent site clicks</div>
                <div className="text-xs text-scl-charcoal/60">Latest {siteClicks.recent.length} outbound clicks to schoolforcommunitylearning.org.</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-scl-cream text-left">
                    <tr>
                      <Th>Name</Th><Th>Email</Th><Th>Link</Th>
                      <Th>Source</Th><Th>utm_content</Th><Th>Clicked at</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {siteClicks.recent.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-scl-charcoal/60">No clicks yet.</td></tr>
                    )}
                    {siteClicks.recent.map((r) => (
                      <tr key={r.id} className="border-t border-scl-border/60" data-testid={`click-row-${r.id}`}>
                        <Td className="font-medium">{(r.first_name || r.last_name) ? `${r.first_name || ""} ${r.last_name || ""}`.trim() : <span className="text-scl-charcoal/40">Anonymous</span>}</Td>
                        <Td>{r.email ? <a href={`mailto:${r.email}`} className="text-scl-teal hover:underline">{r.email}</a> : <span className="text-scl-charcoal/30">—</span>}</Td>
                        <Td><span className="px-2 py-0.5 rounded-full bg-scl-mustard/40 text-scl-charcoal text-[10px] font-bold uppercase tracking-wider">{r.link_type}</span></Td>
                        <Td className="text-xs">{r.source_path || <span className="text-scl-charcoal/30">—</span>}</Td>
                        <Td><span className="font-mono text-xs">{r.utm_content || <span className="text-scl-charcoal/40">—</span>}</span></Td>
                        <Td className="text-xs text-scl-charcoal/70">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="qrcodes" data-testid="qrcodes-panel">
            <div className="bg-white rounded-2xl shadow-warm-sm p-5 mb-4">
              <div className="flex items-center gap-2 mb-1">
                <QrCode className="w-5 h-5 text-scl-teal" />
                <div className="font-serif text-xl font-bold text-scl-charcoal">Event QR codes</div>
              </div>
              <p className="text-sm text-scl-charcoal/70">Print or share these QR codes at your next event. Each is pre-tagged with UTMs so attendance shows up in the Site Clicks tab and the Participants tab.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EventQRCard
                label="Kindness Challenge"
                accent="bg-scl-coral/10 text-scl-coral"
                url="https://scl-kindness.emergent.host/go/kindness?utm_source=event&utm_medium=qr&utm_campaign=scl_enrollment&utm_content=kindness_qr"
                testid="qr-kindness"
                downloadFile="scl-kindness-qr.png"
              />
              <EventQRCard
                label="Family Activity Spinner"
                accent="bg-scl-teal/10 text-scl-teal"
                url="https://scl-kindness.emergent.host/go/activity?utm_source=event&utm_medium=qr&utm_campaign=scl_enrollment&utm_content=activity_qr"
                testid="qr-activity"
                downloadFile="scl-activity-qr.png"
              />
            </div>
          </TabsContent>

          <TabsContent value="enrollments" data-testid="enrollments-panel">
            <EnrollmentsPanel password={password} />
          </TabsContent>

          <TabsContent value="staffuploads" data-testid="staffuploads-panel">
            <StaffUploadsPanel password={password} />
          </TabsContent>

          <TabsContent value="familyinvites" data-testid="familyinvites-panel">
            {isSuperAdmin && <FamilyInvitesPanel password={password} adminEmail={adminEmail} />}
          </TabsContent>

          <TabsContent value="staffsignins" data-testid="staffsignins-panel">
            {isSuperAdmin && <StaffSigninsPanel password={password} adminEmail={adminEmail} />}
          </TabsContent>

          <TabsContent value="digest" data-testid="digest-panel">
            {isSuperAdmin && <ParentDigestPanel password={password} adminEmail={adminEmail} />}
          </TabsContent>

          <TabsContent value="tours" data-testid="tours-panel">
            {isSuperAdmin && <ToursPanel password={password} adminEmail={adminEmail} />}
          </TabsContent>

          <TabsContent value="eventocr" data-testid="eventocr-panel">
            <EventOcrPanel password={password} />
          </TabsContent>


        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-warm-sm flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${bg}`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">{label}</div>
        <div className="font-serif text-2xl font-bold text-scl-charcoal leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}

// ============================================================================
// Google Sign-In scaffold — restricted to sclk-8.org domain.
// Renders as a disabled state until GOOGLE_OAUTH_CLIENT_ID is set in .env.
// Once set, uses Google Identity Services one-tap / button, POSTs the credential
// to /api/staff/google-signin which verifies the domain and returns an admin token.
// ============================================================================
function GoogleSignInScaffold({ onAuth }) {
  const [cfg, setCfg] = useState({ allowed_domain: "sclk-8.org" });
  useEffect(() => {
    axios.get(`${API}/staff/oauth-config`).then((r) => {
      setCfg({ allowed_domain: r.data.allowed_domain || "sclk-8.org" });
    }).catch(() => {});
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startEmergentAuth = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mt-5 pt-5 border-t border-scl-border" data-testid="google-signin-scaffold">
      <div className="text-[11px] uppercase tracking-widest text-scl-charcoal/50 font-semibold mb-2 text-center">or</div>
      <button
        type="button"
        onClick={startEmergentAuth}
        data-testid="google-signin-button"
        className="w-full h-11 rounded-full border border-scl-border bg-white text-scl-charcoal text-sm font-medium flex items-center justify-center gap-2 hover:bg-scl-cream shadow-warm-sm"
      >
        <GoogleG /> Sign in with Google (@{cfg.allowed_domain})
      </button>
      <p className="mt-2 text-[10px] text-scl-charcoal/45 text-center">Restricted to @{cfg.allowed_domain} accounts.</p>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

// ============================================================================
// Staff Uploads — config-driven "Add (+)" grid of upload types.
// Reads /api/staff/upload-types and renders one card per type. Clicking a card
// opens a per-type form (fields defined in the registry) that POSTs to
// /api/staff/uploads/{type_id} which forwards a 13-column row to the Apps
// Script webhook logging to the "OCR Uploads" tab.
// ============================================================================
function StaffUploadsPanel({ password }) {
  const [types, setTypes] = useState([]);
  const [webhookOk, setWebhookOk] = useState(false);
  const [active, setActive] = useState(null); // active upload-type config
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!password) return;
    setLoading(true);
    axios.get(`${API}/staff/upload-types`, { params: { password } })
      .then((r) => { setTypes(r.data.types || []); setWebhookOk(!!r.data.webhook_configured); })
      .catch(() => toast.error("Couldn't load upload types"))
      .finally(() => setLoading(false));
  }, [password]);
  if (loading) return <div className="p-6 text-center text-scl-charcoal/60">Loading…</div>;
  if (active) return <UploadTypeForm cfg={active} password={password} onBack={() => setActive(null)} />;
  return (
    <div>
      <div className="bg-white rounded-2xl shadow-warm-sm p-5 mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="font-serif text-xl font-bold text-scl-charcoal mb-1">Staff uploads</div>
          <p className="text-sm text-scl-charcoal/70">Every upload posts to the shared Apps Script webhook → <b>OCR Uploads</b> tab in the tracker.</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ${webhookOk ? "bg-scl-teal/15 text-scl-teal" : "bg-scl-mustard/40 text-scl-charcoal/70"}`} data-testid="staff-webhook-status">
          {webhookOk ? "Webhook live" : "Webhook not configured"}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {types.map((tp) => (
          <button
            key={tp.id}
            onClick={() => setActive(tp)}
            data-testid={`upload-type-${tp.id}`}
            className="text-left bg-white rounded-2xl p-5 shadow-warm-sm hover:shadow-warm transition-shadow active:scale-[0.99] flex gap-3 items-start min-h-[100px]"
          >
            <div className="w-11 h-11 rounded-xl bg-scl-teal/10 flex items-center justify-center flex-shrink-0">
              <Plus className="w-5 h-5 text-scl-teal" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-serif text-lg font-bold text-scl-charcoal leading-tight mb-1">{tp.label}</div>
              <p className="text-xs text-scl-charcoal/60 leading-relaxed">{tp.description}</p>
              <div className="mt-2 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-scl-charcoal/50 font-semibold">
                <span className="px-2 py-0.5 rounded-full bg-scl-cream">{tp.flow}</span>
                <span className="px-2 py-0.5 rounded-full bg-scl-cream">max {tp.max_mb}MB</span>
              </div>
            </div>
          </button>
        ))}
      </div>
      <p className="mt-4 text-[11px] text-scl-charcoal/50">To add a new upload type: edit <code className="font-mono bg-scl-cream px-1.5 py-0.5 rounded">/app/backend/staff_uploads.py</code> and add an entry to <code className="font-mono bg-scl-cream px-1.5 py-0.5 rounded">UPLOAD_TYPES</code>. No new route required.</p>
    </div>
  );
}

function UploadTypeForm({ cfg, password, onBack }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [fields, setFields] = useState({});
  const [consent, setConsent] = useState(!!cfg.consent_default);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showCropper, setShowCropper] = useState(false);
  const [isCropped, setIsCropped] = useState(false);
  const [croppedPreset, setCroppedPreset] = useState("");
  const [studentLinkStatus, setStudentLinkStatus] = useState(null);
  // Camera capture: only meaningful when the type accepts images. Desktop browsers ignore it.
  const acceptsImages = (cfg.accepts || "").includes("image/");
  const capture = acceptsImages ? "environment" : undefined;
  // Types that route to the parent portal via subject_name match.
  const parentPortalType = ["homework", "student_work_example"].includes(cfg.id) && (cfg.fields || []).some((f) => f.key === "subject_name");
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setIsCropped(false);
    setCroppedPreset("");
    if (f.type.startsWith("image/")) setPreview(URL.createObjectURL(f)); else setPreview("");
  };
  const onCroppedFile = (croppedFile, meta = {}) => {
    setFile(croppedFile);
    setPreview(URL.createObjectURL(croppedFile));
    setIsCropped(true);
    setCroppedPreset(meta.preset || "");
    setShowCropper(false);
    toast.success(meta.preset === "kindness_wall" ? "Kindness Wall applied" : "Cropped for social");
  };
  // Debounced student name → family invite lookup
  const nameToCheck = parentPortalType ? (fields.subject_name || "").trim() : "";
  useEffect(() => {
    if (!parentPortalType) return;
    if (!nameToCheck) { setStudentLinkStatus(null); return; }
    let cancelled = false;
    setStudentLinkStatus({ checking: true, matches: [], name: nameToCheck });
    const h = setTimeout(() => {
      axios.get(`${API}/admin/family-invites/lookup`, { params: { password, student_name: nameToCheck } })
        .then((r) => { if (!cancelled) setStudentLinkStatus({ checking: false, matches: r.data.matches || [], name: nameToCheck }); })
        .catch(() => { if (!cancelled) setStudentLinkStatus({ checking: false, matches: [], name: nameToCheck }); });
    }, 350);
    return () => { cancelled = true; clearTimeout(h); };
  }, [nameToCheck, parentPortalType, password]);
  const setField = (k, v) => {
    setFields((s) => ({ ...s, [k]: v }));
    if (fieldErrors[k]) setFieldErrors((s) => { const n = { ...s }; delete n[k]; return n; });
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose or capture a file"); return; }
    const nextErrors = {};
    for (const f of cfg.fields) {
      if (f.required && !(fields[f.key] || "").trim()) {
        nextErrors[f.key] = `${f.label} is required`;
      }
    }
    if (Object.keys(nextErrors).length) { setFieldErrors(nextErrors); return; }
    setSubmitting(true);
    try {
      // Signed-in staff email captured from Emergent Auth callback (see AdminScreen useEffect).
      const submittedBy = sessionStorage.getItem("scl_admin_email") || "";
      const fd = new FormData();
      fd.append("password", password);
      fd.append("submitted_by", submittedBy);
      fd.append("subject_name", fields.subject_name || "");
      fd.append("grade_class", fields.grade_class || "");
      fd.append("email", fields.email || "");
      fd.append("phone", fields.phone || "");
      // For event_ocr, blend event_name into notes so it lands in the sheet's Notes column
      const combinedNotes = cfg.id === "event_ocr" && fields.event_name
        ? `[${fields.event_name}] ${fields.notes || ""}`.trim()
        : (fields.notes || "");
      fd.append("notes", combinedNotes);
      fd.append("consent", consent ? "true" : "false");
      if (croppedPreset) fd.append("preset", croppedPreset);
      fd.append("file", file);
      const r = await axios.post(`${API}/staff/uploads/${cfg.id}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
      setDone(r.data);
      toast.success(r.data.webhook ? "Uploaded and logged to tracker" : "Uploaded (webhook not configured)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setSubmitting(false); }
  };
  if (done) {
    return (
      <div className="bg-white rounded-2xl shadow-warm-sm p-6 text-center" data-testid="upload-success">
        <div className="w-14 h-14 rounded-full bg-scl-teal/10 flex items-center justify-center mx-auto mb-3"><CheckCircle2 className="w-7 h-7 text-scl-teal" /></div>
        <div className="font-serif text-2xl font-bold text-scl-charcoal mb-1">Uploaded</div>
        <p className="text-sm text-scl-charcoal/70 mb-4">{done.webhook ? "Row posted to the tracker's OCR Uploads tab." : "Row saved locally. Webhook is not configured — set APPS_SCRIPT_WEBHOOK_URL to enable live tracker sync."}</p>
        {done.file_link && <a href={done.file_link} target="_blank" rel="noreferrer" className="inline-block text-sm text-scl-teal hover:underline break-all mb-4">{done.file_link}</a>}
        <div className="flex gap-2 justify-center">
          <button onClick={() => { setDone(null); setFile(null); setPreview(""); setFields({}); setConsent(!!cfg.consent_default); }} data-testid="upload-another" className="h-11 px-5 rounded-full bg-scl-teal text-white font-semibold text-sm">Upload another</button>
          <button onClick={onBack} className="h-11 px-5 rounded-full border border-scl-border text-scl-charcoal font-medium text-sm hover:bg-scl-cream">Back to upload types</button>
        </div>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-warm-sm p-5" data-testid="upload-type-form">
      <button type="button" onClick={onBack} className="text-xs text-scl-charcoal/60 hover:text-scl-teal mb-3 flex items-center gap-1" data-testid="upload-back-btn">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>
      <div className="font-serif text-xl font-bold text-scl-charcoal mb-1">{cfg.label}</div>
      <p className="text-sm text-scl-charcoal/70 mb-4">{cfg.description}</p>

      <label className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">File</label>
      <input
        type="file"
        accept={cfg.accepts}
        capture={capture}
        onChange={onFile}
        data-testid="upload-file"
        className="mt-1.5 mb-1 block w-full text-sm text-scl-charcoal/80 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-scl-teal file:text-white file:text-sm file:font-semibold file:cursor-pointer hover:file:brightness-110"
      />
      <p className="text-[10px] text-scl-charcoal/50 mb-3">
        {acceptsImages ? "On mobile, tapping this opens your camera. " : ""}Accepts: {cfg.accepts}. Max {cfg.max_mb} MB.
      </p>
      {preview && (
        <div className="mb-3">
          <div className="relative inline-block">
            <img src={preview} alt="preview" className="max-h-40 rounded-xl border border-scl-border" data-testid="upload-preview" />
            {isCropped && (
              <span className="absolute top-1 left-1 px-2 py-0.5 rounded-full bg-scl-coral text-white text-[9px] font-bold uppercase tracking-widest">SCL cropped</span>
            )}
          </div>
          {acceptsImages && file && file.type.startsWith("image/") && (
            <button
              type="button"
              onClick={() => setShowCropper(true)}
              data-testid="open-cropper-btn"
              className="ml-3 h-9 px-3 rounded-full border border-scl-coral text-scl-coral text-xs font-semibold inline-flex items-center gap-1 hover:bg-scl-coral/10 align-top"
            >
              <Crop className="w-3.5 h-3.5" /> {isCropped ? "Re-crop" : "Crop for social"}
            </button>
          )}
        </div>
      )}
      {showCropper && file && (
        <InstagramCropper file={file} onCancel={() => setShowCropper(false)} onConfirm={onCroppedFile} />
      )}

      {cfg.fields.map((f) => (
        <div key={f.key} className="mb-3">
          <label className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">{f.label}{f.required && <span className="text-scl-brick"> *</span>}</label>
          {f.type === "textarea" ? (
            <textarea
              value={fields[f.key] || ""}
              onChange={(e) => setField(f.key, e.target.value)}
              data-testid={`upload-field-${f.key}`}
              className={`mt-1.5 w-full rounded-xl border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-scl-teal/30 ${fieldErrors[f.key] ? "border-scl-brick" : "border-scl-border"}`}
              rows={3}
            />
          ) : (
            <Input
              value={fields[f.key] || ""}
              onChange={(e) => setField(f.key, e.target.value)}
              data-testid={`upload-field-${f.key}`}
              className={`mt-1.5 h-11 rounded-xl bg-white ${fieldErrors[f.key] ? "border-scl-brick" : "border-scl-border"}`}
            />
          )}
          {fieldErrors[f.key] && (
            <p data-testid={`upload-field-${f.key}-error`} className="mt-1 text-[11px] text-scl-brick">{fieldErrors[f.key]}</p>
          )}
          {parentPortalType && f.key === "subject_name" && studentLinkStatus && studentLinkStatus.name && (
            <div
              data-testid="student-link-hint"
              className={`mt-2 rounded-xl px-3 py-2 text-[12px] leading-snug ${
                studentLinkStatus.checking
                  ? "bg-scl-cream text-scl-charcoal/60"
                  : studentLinkStatus.matches.length > 0
                    ? "bg-scl-teal/10 text-scl-teal"
                    : "bg-scl-mustard/25 text-scl-charcoal"
              }`}
            >
              {studentLinkStatus.checking
                ? "Checking family link…"
                : studentLinkStatus.matches.length > 0
                  ? <>
                      <b>Parent linked.</b> This will appear instantly in the Parent Portal for{" "}
                      {studentLinkStatus.matches.map((m, i) => (
                        <span key={m.id}>{i > 0 ? ", " : ""}<span className="font-mono">{m.parent_email}</span></span>
                      ))}.
                    </>
                  : <>No parent is linked to <b>{studentLinkStatus.name}</b> yet. Ask a super admin to add a Family Invite (or upload anyway — it&apos;ll be visible once linked).</>}
            </div>
          )}
        </div>
      ))}

      <label className="flex items-start gap-2 mb-4 cursor-pointer">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="upload-consent" className="mt-1 accent-scl-teal w-4 h-4" />
        <span className="text-sm text-scl-charcoal/80 leading-snug">{cfg.consent_label}</span>
      </label>

      <button type="submit" disabled={submitting || !file} data-testid="upload-submit" className="h-11 px-5 rounded-full bg-scl-coral text-white font-semibold text-sm flex items-center gap-2 hover:brightness-105 disabled:opacity-60">
        {submitting ? "Uploading…" : "Upload"}
      </button>
    </form>
  );
}

// ============================================================================
// Enrollments panel — surfaces in-app intake form submissions
// ============================================================================
function EnrollmentsPanel({ password }) {
  const [data, setData] = useState({ total: 0, high_intent: 0, low_intent: 0, recent: [] });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!password) return;
    setLoading(true);
    axios.get(`${API}/admin/enrollments`, { params: { password, limit: 1000 } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [password]);
  const exportCsv = () => {
    const rows = data.recent || [];
    if (rows.length === 0) { toast.error("No enrollments yet"); return; }
    const headers = ["Name","Email","Phone","Child Age","Grade","Notes","Path","Intent","Requested Guide","Language","utm_source","utm_medium","utm_campaign","utm_content","Created At"];
    const esc = (v) => { if (v == null) return ""; const s = String(v).replace(/"/g, '""'); return /[",\n]/.test(s) ? `"${s}"` : s; };
    const csv = [headers.join(",")].concat(rows.map((r) => [
      esc(r.name), esc(r.email), esc(r.phone), esc(r.child_age), esc(r.grade), esc(r.notes),
      esc(r.path), esc(r.intent), esc(r.requested_guide), esc(r.language),
      esc(r.utm_source), esc(r.utm_medium), esc(r.utm_campaign), esc(r.utm_content),
      esc(r.created_at),
    ].join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `scl-enrollments-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} enrollments`);
  };
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <StatCard icon={Users} label="Total enrollments" value={data.total || 0} color="text-scl-teal" bg="bg-scl-teal/10" />
        <StatCard icon={Award} label="High intent (Kindness)" value={data.high_intent || 0} color="text-scl-coral" bg="bg-scl-coral/10" />
        <StatCard icon={CheckCircle2} label="Low intent (Spinner)" value={data.low_intent || 0} color="text-scl-charcoal" bg="bg-scl-mustard/40" />
      </div>

      <div className="bg-white rounded-2xl shadow-warm-sm p-4 mb-4 flex items-center justify-between gap-3">
        <div className="text-xs text-scl-charcoal/70">In-app intake form submissions. High-intent (Kindness) and low-intent (Spinner) are clearly tagged for tracker import.</div>
        <button onClick={exportCsv} data-testid="export-enrollments" className="h-10 px-4 rounded-full bg-scl-teal text-white text-sm font-semibold flex items-center gap-2 hover:brightness-110">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden" data-testid="enrollments-table">
        <div className="p-4 border-b border-scl-border">
          <div className="font-serif text-lg font-bold text-scl-charcoal">Recent enrollments</div>
          <div className="text-xs text-scl-charcoal/60">Latest {data.recent.length} submissions.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-scl-cream text-left">
              <tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Grade</Th><Th>Age</Th><Th>Notes</Th><Th>Intent</Th><Th>Guide</Th><Th>utm_content</Th><Th>When</Th></tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="p-6 text-center text-scl-charcoal/60">Loading…</td></tr>}
              {!loading && data.recent.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-scl-charcoal/60">No enrollments yet.</td></tr>}
              {data.recent.map((r) => (
                <tr key={r.id} className="border-t border-scl-border/60" data-testid={`enrollment-row-${r.id}`}>
                  <Td className="font-medium">{r.name || <span className="text-scl-charcoal/30">—</span>}</Td>
                  <Td><a href={`mailto:${r.email}`} className="text-scl-teal hover:underline">{r.email}</a></Td>
                  <Td className="text-xs">{r.phone || <span className="text-scl-charcoal/30">—</span>}</Td>
                  <Td className="text-xs">{r.grade || <span className="text-scl-charcoal/30">—</span>}</Td>
                  <Td className="text-xs">{r.child_age || <span className="text-scl-charcoal/30">—</span>}</Td>
                  <Td className="text-xs max-w-[200px] truncate" title={r.notes}>{r.notes || <span className="text-scl-charcoal/30">—</span>}</Td>
                  <Td>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${r.intent === "high" ? "bg-scl-coral/15 text-scl-coral" : "bg-scl-mustard/40 text-scl-charcoal/80"}`}>{r.intent}</span>
                  </Td>
                  <Td className="text-xs">{r.requested_guide}</Td>
                  <Td className="font-mono text-xs">{r.utm_content || <span className="text-scl-charcoal/30">—</span>}</Td>
                  <Td className="text-xs text-scl-charcoal/70">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Event OCR panel — upload sign-in sheet photo, review rows, send packets
// ============================================================================
function EventOcrPanel({ password }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [ocring, setOcring] = useState(false);
  const [sending, setSending] = useState(false);
  const [eventName, setEventName] = useState("");
  const [preview, setPreview] = useState("");

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setRows([]);
  };
  const runOcr = async () => {
    if (!file) { toast.error("Choose a photo first"); return; }
    setOcring(true);
    try {
      const fd = new FormData();
      fd.append("password", password);
      fd.append("image", file);
      const res = await axios.post(`${API}/admin/event-ocr`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      setRows(res.data.rows || []);
      if ((res.data.rows || []).length === 0) toast.info("No rows detected — try a clearer photo");
      else toast.success(`Extracted ${res.data.rows.length} rows — review below`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OCR failed");
    } finally { setOcring(false); }
  };
  const updateRow = (i, field, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [field]: v } : r));
  const removeRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));
  const sendPackets = async () => {
    const withEmail = rows.filter((r) => r.email && r.email.includes("@"));
    if (withEmail.length === 0) { toast.error("At least one row needs an email"); return; }
    if (!window.confirm(`Send packet emails to ${withEmail.length} parents?`)) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/event-leads/send`, { password, rows, event_name: eventName || "" }, { timeout: 60000 });
      const sent = (res.data.results || []).filter((x) => x.ok).length;
      toast.success(`Sent ${sent} of ${res.data.processed} packet emails`);
      setRows([]); setFile(null); setPreview("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };
  return (
    <div>
      <div className="bg-white rounded-2xl shadow-warm-sm p-5 mb-4">
        <div className="font-serif text-xl font-bold text-scl-charcoal mb-1">Event sign-in OCR</div>
        <p className="text-sm text-scl-charcoal/70 mb-4">Upload a photo of a paper sign-in sheet. We&apos;ll OCR handwritten names, emails, phones, grades, and notes. Review before sending packets.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">Sign-in sheet photo (JPEG/PNG/WEBP, max 10 MB)</label>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} data-testid="ocr-file-input" className="mt-1.5 block w-full text-sm text-scl-charcoal/80 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-scl-teal file:text-white file:text-sm file:font-semibold file:cursor-pointer hover:file:brightness-110" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">Event name (optional)</label>
            <Input value={eventName} onChange={(e) => setEventName(e.target.value)} data-testid="ocr-event-name" placeholder="e.g. Black Expo 2026" className="mt-1.5 h-11 rounded-xl border-scl-border bg-white" />
          </div>
        </div>
        {preview && <img src={preview} alt="Sign-in sheet preview" className="max-h-48 rounded-xl border border-scl-border mb-3" />}
        <button onClick={runOcr} disabled={!file || ocring} data-testid="ocr-run-btn" className="h-11 px-5 rounded-full bg-scl-teal text-white font-semibold text-sm flex items-center gap-2 hover:brightness-110 disabled:opacity-60">
          {ocring ? "Reading photo…" : "Extract rows with OCR"}
        </button>
      </div>

      {rows.length > 0 && (
        <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden mb-4" data-testid="ocr-review-table">
          <div className="p-4 border-b border-scl-border flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-serif text-lg font-bold text-scl-charcoal">Review — {rows.length} row(s)</div>
              <div className="text-xs text-scl-charcoal/60">Fix any OCR mistakes before sending packet emails.</div>
            </div>
            <button onClick={sendPackets} disabled={sending} data-testid="ocr-send-btn" className="h-11 px-5 rounded-full bg-scl-coral text-white font-semibold text-sm flex items-center gap-2 hover:brightness-105 disabled:opacity-60">
              {sending ? "Sending…" : "Send packet emails"}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-scl-cream text-left">
                <tr><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Grade</Th><Th>Age</Th><Th>Notes</Th><Th>Lang</Th><Th></Th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-scl-border/60">
                    <Td><input value={r.name || ""} onChange={(e) => updateRow(i, "name", e.target.value)} data-testid={`ocr-row-${i}-name`} className="w-full bg-transparent focus:outline-none focus:bg-white rounded px-2 py-1" /></Td>
                    <Td><input value={r.email || ""} onChange={(e) => updateRow(i, "email", e.target.value)} data-testid={`ocr-row-${i}-email`} className="w-full bg-transparent focus:outline-none focus:bg-white rounded px-2 py-1" /></Td>
                    <Td><input value={r.phone || ""} onChange={(e) => updateRow(i, "phone", e.target.value)} data-testid={`ocr-row-${i}-phone`} className="w-full bg-transparent focus:outline-none focus:bg-white rounded px-2 py-1" /></Td>
                    <Td><input value={r.grade || ""} onChange={(e) => updateRow(i, "grade", e.target.value)} className="w-16 bg-transparent focus:outline-none focus:bg-white rounded px-2 py-1" /></Td>
                    <Td><input value={r.child_age || ""} onChange={(e) => updateRow(i, "child_age", e.target.value)} className="w-12 bg-transparent focus:outline-none focus:bg-white rounded px-2 py-1" /></Td>
                    <Td><input value={r.notes || ""} onChange={(e) => updateRow(i, "notes", e.target.value)} className="w-full bg-transparent focus:outline-none focus:bg-white rounded px-2 py-1" /></Td>
                    <Td>
                      <select value={r.language || "en"} onChange={(e) => updateRow(i, "language", e.target.value)} className="bg-transparent focus:outline-none focus:bg-white rounded px-1 py-1">
                        <option value="en">EN</option><option value="es">ES</option>
                      </select>
                    </Td>
                    <Td><button onClick={() => removeRow(i)} className="text-scl-brick text-xs hover:underline">Remove</button></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function EventQRCard({ label, accent, url, testid, downloadFile }) {
  const wrapRef = useRef(null);
  const handleDownload = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) { toast.error("QR not ready — try again"); return; }
    // Draw QR + label onto a padded canvas so the PNG is print-ready
    const pad = 48;
    const labelH = 80;
    const out = document.createElement("canvas");
    out.width = canvas.width + pad * 2;
    out.height = canvas.height + pad * 2 + labelH;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, pad, pad);
    ctx.fillStyle = "#2b2b2b";
    ctx.font = "bold 32px Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText(label, out.width / 2, canvas.height + pad + 48);
    const link = document.createElement("a");
    link.download = downloadFile;
    link.href = out.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Downloaded ${downloadFile}`);
  };
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("URL copied"); }
    catch { toast.error("Copy failed"); }
  };
  return (
    <div className="bg-white rounded-2xl shadow-warm-sm p-5 flex flex-col" data-testid={testid}>
      <div className={`inline-flex items-center self-start px-3 py-1 rounded-full text-[11px] uppercase tracking-widest font-semibold mb-3 ${accent}`}>{label}</div>
      <div ref={wrapRef} className="rounded-xl bg-white border border-scl-border/60 p-4 flex items-center justify-center mb-4">
        <QRCodeCanvas value={url} size={256} level="H" includeMargin={false} bgColor="#ffffff" fgColor="#2b2b2b" />
      </div>
      <div className="text-[11px] text-scl-charcoal/60 mb-1 font-semibold uppercase tracking-widest">Destination URL</div>
      <div className="text-xs text-scl-charcoal/80 break-all mb-4 font-mono">{url}</div>
      <div className="flex gap-2 mt-auto">
        <button
          onClick={handleDownload}
          data-testid={`${testid}-download`}
          className="flex-1 h-11 rounded-full bg-scl-teal text-white font-semibold text-sm flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all min-h-[44px]"
        >
          <Download className="w-4 h-4" /> Download PNG
        </button>
        <button
          onClick={copyUrl}
          data-testid={`${testid}-copy`}
          className="h-11 px-4 rounded-full border border-scl-border text-scl-charcoal font-medium text-sm hover:bg-scl-cream min-h-[44px]"
        >
          Copy URL
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Family Invites — grant a parent email access to a student's homework feed
// ============================================================================
function FamilyInvitesPanel({ password, adminEmail }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ parent_email: "", student_name: "", grade_class: "" });
  const [submitting, setSubmitting] = useState(false);
  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/family-invites`, { params: { password } })
      .then((r) => setRows(r.data.recent || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (password) load(); }, [password]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_email || !form.student_name) { toast.error("Parent email and student name are required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/family-invites`, form, { params: { password, admin_email: adminEmail } });
      toast.success(`Linked ${form.parent_email} → ${form.student_name}`);
      setForm({ parent_email: "", student_name: "", grade_class: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create invite");
    } finally { setSubmitting(false); }
  };
  const removeInvite = async (r) => {
    if (!window.confirm(`Remove ${r.parent_email} → ${r.student_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/family-invites/${r.id}`, { params: { password, admin_email: adminEmail } });
      toast.success("Invite removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };
  return (
    <div>
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-warm-sm p-5 mb-4" data-testid="family-invite-form">
        <div className="font-serif text-xl font-bold text-scl-charcoal mb-1">Grant family access</div>
        <p className="text-sm text-scl-charcoal/70 mb-4">A parent who signs in at <b>/parent</b> with the email below will see homework, student work, and classroom photos tagged with this student&apos;s name.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">Parent email *</label>
            <Input value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} data-testid="fi-parent-email" type="email" placeholder="parent@example.com" className="mt-1.5 h-11 rounded-xl border-scl-border bg-white" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">Student name *</label>
            <Input value={form.student_name} onChange={(e) => setForm({ ...form, student_name: e.target.value })} data-testid="fi-student-name" placeholder="First and last" className="mt-1.5 h-11 rounded-xl border-scl-border bg-white" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">Grade / Class</label>
            <Input value={form.grade_class} onChange={(e) => setForm({ ...form, grade_class: e.target.value })} data-testid="fi-grade" placeholder="e.g. 3rd" className="mt-1.5 h-11 rounded-xl border-scl-border bg-white" />
          </div>
        </div>
        <button type="submit" disabled={submitting} data-testid="fi-submit" className="mt-4 h-11 px-5 rounded-full bg-scl-coral text-white font-semibold text-sm hover:brightness-105 disabled:opacity-60">
          {submitting ? "Linking…" : "Create invite"}
        </button>
      </form>

      <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden" data-testid="fi-list">
        <div className="p-4 border-b border-scl-border">
          <div className="font-serif text-lg font-bold text-scl-charcoal">Active family links</div>
          <div className="text-xs text-scl-charcoal/60">{rows.length} link(s). Parents access the homework portal at <code className="font-mono bg-scl-cream px-1.5 py-0.5 rounded">/parent</code>.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-scl-cream text-left"><tr><Th>Parent email</Th><Th>Student</Th><Th>Grade</Th><Th>Status</Th><Th>Created</Th><Th></Th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-6 text-center text-scl-charcoal/60">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-scl-charcoal/60">No family invites yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-scl-border/60" data-testid={`fi-row-${r.id}`}>
                  <Td><a href={`mailto:${r.parent_email}`} className="text-scl-teal hover:underline">{r.parent_email}</a></Td>
                  <Td className="font-medium">{r.student_name}</Td>
                  <Td className="text-xs">{r.grade_class || <span className="text-scl-charcoal/30">—</span>}</Td>
                  <Td><span className="px-2 py-0.5 rounded-full bg-scl-teal/15 text-scl-teal text-[10px] font-bold uppercase tracking-wider">{r.status}</span></Td>
                  <Td className="text-xs text-scl-charcoal/70">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</Td>
                  <Td className="text-right">
                    <button onClick={() => removeInvite(r)} data-testid={`fi-delete-${r.id}`} title="Remove access" className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-scl-brick/10 text-scl-brick">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Staff Sign-ins panel (super admin only) — audit trail of Google sign-ins
// ============================================================================
function StaffSigninsPanel({ password, adminEmail }) {
  const [data, setData] = useState({ total: 0, unique_emails: 0, recent: [] });
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!password) return;
    setLoading(true);
    axios.get(`${API}/admin/staff-signins`, { params: { password, admin_email: adminEmail } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load staff sign-ins"))
      .finally(() => setLoading(false));
  }, [password, adminEmail]);
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <StatCard icon={Users} label="Total sign-ins" value={data.total || 0} color="text-scl-teal" bg="bg-scl-teal/10" />
        <StatCard icon={CheckCircle2} label="Unique staff" value={data.unique_emails || 0} color="text-scl-coral" bg="bg-scl-coral/10" />
        <StatCard icon={ShieldCheck} label="Super admins seen" value={(data.recent || []).filter((r) => r.is_super_admin).length} color="text-scl-brick" bg="bg-scl-brick/10" />
      </div>
      <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden" data-testid="staff-signins-list">
        <div className="p-4 border-b border-scl-border">
          <div className="font-serif text-lg font-bold text-scl-charcoal">Recent staff sign-ins</div>
          <div className="text-xs text-scl-charcoal/60">Every Google-verified admin login is logged here.</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-scl-cream text-left"><tr><Th>Email</Th><Th>Name</Th><Th>Role</Th><Th>Provider</Th><Th>When</Th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="p-6 text-center text-scl-charcoal/60">Loading…</td></tr>}
              {!loading && (data.recent || []).length === 0 && <tr><td colSpan={5} className="p-6 text-center text-scl-charcoal/60">No sign-ins yet.</td></tr>}
              {(data.recent || []).map((r) => (
                <tr key={r.id} className="border-t border-scl-border/60" data-testid={`ss-row-${r.id}`}>
                  <Td><span className="font-medium text-scl-teal">{r.email}</span></Td>
                  <Td>{r.name || <span className="text-scl-charcoal/30">—</span>}</Td>
                  <Td>
                    {r.is_super_admin
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-scl-brick text-white text-[10px] font-bold uppercase tracking-widest"><ShieldCheck className="w-3 h-3" /> Super</span>
                      : <span className="text-xs text-scl-charcoal/50">Admin</span>}
                  </Td>
                  <Td className="text-xs">{r.provider || "google"}</Td>
                  <Td className="text-xs text-scl-charcoal/70">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Parent Digest panel (super admin only) — preview and manually trigger the
// weekly Friday homework digest. A platform cron runs this automatically at
// 4pm America/Indiana/Indianapolis every Friday.
// ============================================================================
function ParentDigestPanel({ password, adminEmail }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [stats, setStats] = useState({ runs: [] });
  const [statsLoading, setStatsLoading] = useState(false);
  const loadStats = () => {
    setStatsLoading(true);
    axios.get(`${API}/admin/digest/stats`, { params: { password, admin_email: adminEmail } })
      .then((r) => setStats(r.data || { runs: [] }))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  };
  useEffect(() => { if (password && adminEmail) loadStats(); }, [password, adminEmail]);
  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/digest/homework/run`, null, {
        params: { password, admin_email: adminEmail, dry_run: true },
      });
      setPreview(r.data);
      toast.success(`Preview ready: ${r.data.parents_matched} parent(s) queued`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };
  const runSend = async () => {
    setBusy(true);
    setConfirmSend(false);
    try {
      const r = await axios.post(`${API}/admin/digest/homework/run`, null, {
        params: { password, admin_email: adminEmail, dry_run: false },
      });
      setPreview(r.data);
      if (r.data.sent > 0) toast.success(`Sent ${r.data.sent} digest email(s)`);
      else if (r.data.skipped > 0) toast.error(`No emails sent — Gmail SMTP not configured yet (skipped ${r.data.skipped})`);
      else toast.info("Nothing to send this week");
      loadStats();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      <div className="bg-white rounded-2xl shadow-warm-sm p-5 mb-5">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-scl-mustard flex items-center justify-center text-scl-charcoal shrink-0">
            <Mail className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="font-serif font-bold text-scl-charcoal text-lg">Parent homework digest</div>
            <p className="text-sm text-scl-charcoal/70 leading-snug mt-1">
              Every Friday at 4:00pm (Indianapolis time), linked parents receive an email summary of every homework note, work example, or classroom photo their child was tagged in that week. Preview safely first — dry-run doesn&apos;t send anything.
            </p>
            <div className="flex gap-2 mt-3 flex-wrap">
              <button onClick={runPreview} disabled={busy} data-testid="digest-preview-btn" className="h-10 px-4 rounded-full bg-scl-teal text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:brightness-105 disabled:opacity-60">
                <Users className="w-4 h-4" /> {busy ? "Working…" : "Preview this week"}
              </button>
              <button onClick={() => setConfirmSend(true)} disabled={busy || !preview || preview.parents_matched === 0} data-testid="digest-send-btn" className="h-10 px-4 rounded-full bg-scl-coral text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:brightness-105 disabled:opacity-60">
                <Mail className="w-4 h-4" /> Send now
              </button>
            </div>
          </div>
        </div>
      </div>

      {confirmSend && preview && (
        <div className="bg-scl-mustard/25 rounded-2xl border border-scl-mustard p-4 mb-4" data-testid="digest-confirm">
          <div className="font-semibold text-scl-charcoal">Send digest to {preview.parents_matched} parent(s)?</div>
          <div className="text-xs text-scl-charcoal/70 mb-3">This will trigger real emails right now (if Gmail SMTP is configured).</div>
          <div className="flex gap-2">
            <button onClick={runSend} data-testid="digest-confirm-yes" className="h-9 px-4 rounded-full bg-scl-coral text-white text-xs font-bold">Yes, send</button>
            <button onClick={() => setConfirmSend(false)} data-testid="digest-confirm-no" className="h-9 px-4 rounded-full border border-scl-border text-xs font-medium">Cancel</button>
          </div>
        </div>
      )}

      {preview && (
        <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden" data-testid="digest-result">
          <div className="p-4 border-b border-scl-border flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-serif text-lg font-bold text-scl-charcoal">
                {preview.dry_run ? "Preview" : "Sent"} · {preview.parents_matched} parent(s)
              </div>
              <div className="text-xs text-scl-charcoal/60">Run at {new Date(preview.run_at).toLocaleString()}</div>
            </div>
            <div className="flex gap-2">
              <span className="px-2 py-0.5 rounded-full bg-scl-teal/15 text-scl-teal text-[10px] font-bold uppercase tracking-widest">Sent: {preview.sent}</span>
              <span className="px-2 py-0.5 rounded-full bg-scl-mustard/40 text-scl-charcoal text-[10px] font-bold uppercase tracking-widest">Skipped: {preview.skipped}</span>
              {preview.errors > 0 && <span className="px-2 py-0.5 rounded-full bg-scl-brick text-white text-[10px] font-bold uppercase tracking-widest">Errors: {preview.errors}</span>}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-scl-cream text-left">
                <tr><Th>Parent</Th><Th>Students</Th><Th className="text-right">Items</Th><Th>Status</Th></tr>
              </thead>
              <tbody>
                {(preview.per_parent || []).length === 0 && (
                  <tr><td colSpan={4} className="p-6 text-center text-scl-charcoal/60">No matching updates this week.</td></tr>
                )}
                {(preview.per_parent || []).map((p, i) => (
                  <tr key={p.parent_email + i} className="border-t border-scl-border/60">
                    <Td><span className="font-medium text-scl-teal">{p.parent_email}</span></Td>
                    <Td className="text-xs">{(p.students || []).join(", ")}</Td>
                    <Td className="text-right font-bold">{p.item_count}</Td>
                    <Td>
                      {p.dry_run
                        ? <span className="text-xs text-scl-charcoal/60">Preview</span>
                        : p.sent
                          ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-scl-teal"><CheckCircle2 className="w-3.5 h-3.5" /> Sent</span>
                          : <span className="text-xs text-scl-brick">{p.reason || p.error || "Skipped"}</span>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Digest Stats Dashboard — opens & clicks per Friday run */}
      <div className="mt-6 bg-white rounded-2xl shadow-warm-sm overflow-hidden" data-testid="digest-stats-panel">
        <div className="p-4 border-b border-scl-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-serif text-lg font-bold text-scl-charcoal">Send history</div>
            <div className="text-xs text-scl-charcoal/60">Opens and clicks tracked per Friday send.</div>
          </div>
          <button onClick={loadStats} disabled={statsLoading} data-testid="digest-stats-refresh" className="h-8 px-3 rounded-full border border-scl-border text-xs font-medium hover:bg-scl-cream disabled:opacity-60">
            {statsLoading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-scl-cream text-left">
              <tr>
                <Th>When</Th>
                <Th className="text-right">Parents</Th>
                <Th className="text-right">Sent</Th>
                <Th className="text-right">Opens</Th>
                <Th className="text-right">Unique opens</Th>
                <Th className="text-right">Clicks</Th>
                <Th className="text-right">Open rate</Th>
              </tr>
            </thead>
            <tbody>
              {statsLoading && <tr><td colSpan={7} className="p-6 text-center text-scl-charcoal/60">Loading…</td></tr>}
              {!statsLoading && (stats.runs || []).length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-scl-charcoal/60">No digest runs yet.</td></tr>
              )}
              {(stats.runs || []).map((run) => {
                const rate = run.sent > 0 ? Math.round((run.unique_opens * 100) / run.sent) : 0;
                return (
                  <tr key={run.id} className="border-t border-scl-border/60" data-testid={`digest-stats-row-${run.id}`}>
                    <Td className="text-xs text-scl-charcoal/80">{run.run_at ? new Date(run.run_at).toLocaleString() : "—"}</Td>
                    <Td className="text-right">{run.parents_matched}</Td>
                    <Td className="text-right font-semibold text-scl-teal">{run.sent}</Td>
                    <Td className="text-right">{run.opens}</Td>
                    <Td className="text-right">{run.unique_opens}</Td>
                    <Td className="text-right">{run.clicks}</Td>
                    <Td className="text-right">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${rate >= 50 ? "bg-scl-teal/15 text-scl-teal" : rate >= 25 ? "bg-scl-mustard/40 text-scl-charcoal" : "bg-scl-cream text-scl-charcoal/60"}`}>
                        {rate}%
                      </span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}


// ============================================================================
// Tours panel (super admin only) — schedule/track SCL tours and trigger the
// 1-hour post-tour follow-up email. A platform cron also runs every 15 min.
// ============================================================================
function ToursPanel({ password, adminEmail }) {
  const [rows, setRows] = useState([]);
  const [intents, setIntents] = useState([]);
  const [openIntents, setOpenIntents] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ family_name: "", family_email: "", tour_at: "", notes: "", language: "en" });
  const [submitting, setSubmitting] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [convertingId, setConvertingId] = useState("");
  const [convertAt, setConvertAt] = useState("");
  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/tours`, { params: { password } }),
      axios.get(`${API}/admin/tour-intents`, { params: { password } }),
    ]).then(([toursR, intentsR]) => {
      setRows(toursR.data?.tours || []);
      setIntents(intentsR.data?.intents || []);
      setOpenIntents(intentsR.data?.open || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (password) load(); }, [password]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.family_name || !form.family_email || !form.tour_at) {
      toast.error("Family name, email, and tour time are all required");
      return;
    }
    setSubmitting(true);
    try {
      // Convert the datetime-local input to ISO with the browser's timezone
      const local = new Date(form.tour_at);
      const iso = local.toISOString();
      await axios.post(`${API}/admin/tours`, { ...form, tour_at: iso }, { params: { password, admin_email: adminEmail } });
      toast.success(`Tour saved for ${form.family_name}`);
      setForm({ family_name: "", family_email: "", tour_at: "", notes: "", language: form.language });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSubmitting(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tour for ${t.family_name}?`)) return;
    try {
      await axios.delete(`${API}/admin/tours/${t.id}`, { params: { password, admin_email: adminEmail } });
      toast.success("Tour deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const dismissIntent = async (intent) => {
    if (!window.confirm(`Dismiss the tour intent from ${intent.family_email}?`)) return;
    try {
      await axios.delete(`${API}/admin/tour-intents/${intent.id}`, { params: { password, admin_email: adminEmail } });
      toast.success("Intent dismissed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const openConvert = (intent) => {
    setConvertingId(intent.id);
    setConvertAt("");
  };
  const cancelConvert = () => { setConvertingId(""); setConvertAt(""); };
  const submitConvert = async (intent) => {
    if (!convertAt) { toast.error("Pick a tour time first"); return; }
    try {
      const iso = new Date(convertAt).toISOString();
      await axios.post(`${API}/admin/tour-intents/${intent.id}/convert`, { tour_at: iso }, {
        params: { password, admin_email: adminEmail },
      });
      toast.success(`Scheduled tour for ${intent.family_name || intent.family_email}`);
      cancelConvert();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Convert failed");
    }
  };

  const runFollowups = async (dry) => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/admin/tours/followups/run`, null, { params: { password, admin_email: adminEmail, dry_run: dry } });
      setLastRun(r.data);
      if (!dry && r.data.sent > 0) toast.success(`Sent ${r.data.sent} follow-up email(s)`);
      else if (!dry && r.data.skipped > 0) toast.error(`Gmail SMTP not configured yet (skipped ${r.data.skipped})`);
      else if (dry) toast.success(`${r.data.candidates} tour(s) ready to email`);
      else toast.info("Nothing to send right now");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  return (
    <div>
      {/* Intents from families who tapped the tour CTA on the intake success screen */}
      {(intents.length > 0 || openIntents > 0) && (
        <div className="bg-scl-mustard/25 rounded-2xl p-4 mb-5 border border-scl-mustard/60" data-testid="tour-intents-section">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-scl-mustard flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-scl-charcoal" />
            </div>
            <div>
              <div className="font-serif font-bold text-scl-charcoal">Requests from families</div>
              <div className="text-xs text-scl-charcoal/70">{openIntents} open · captured when a family taps "Book a tour" right after downloading the guide.</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            {intents.filter((i) => i.status === "requested").map((intent) => (
              <div key={intent.id} className="bg-white rounded-xl p-3 shadow-warm-sm" data-testid={`tour-intent-${intent.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-scl-charcoal text-sm truncate">{intent.family_name || "(no name)"}</div>
                    <a href={`mailto:${intent.family_email}`} className="text-scl-teal text-xs hover:underline">{intent.family_email}</a>
                    <div className="text-[10px] text-scl-charcoal/60 mt-1 uppercase tracking-widest">
                      {intent.language || "en"}{intent.utm_source ? ` · ${intent.utm_source}` : ""}
                      {intent.source_path ? ` · ${intent.source_path}` : ""}
                    </div>
                  </div>
                  <button onClick={() => dismissIntent(intent)} data-testid={`tour-intent-dismiss-${intent.id}`} title="Dismiss" className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-scl-brick/10 text-scl-brick shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {convertingId === intent.id ? (
                  <div className="mt-2 flex items-end gap-2 flex-wrap">
                    <div className="flex-1 min-w-[180px]">
                      <label className="text-[10px] uppercase tracking-widest text-scl-charcoal/60 font-semibold">Tour time</label>
                      <Input type="datetime-local" value={convertAt} onChange={(e) => setConvertAt(e.target.value)} data-testid={`tour-intent-when-${intent.id}`} />
                    </div>
                    <button onClick={() => submitConvert(intent)} data-testid={`tour-intent-save-${intent.id}`} className="h-10 px-3 rounded-full bg-scl-coral text-white text-xs font-bold">Save</button>
                    <button onClick={cancelConvert} data-testid={`tour-intent-cancel-${intent.id}`} className="h-10 px-3 rounded-full border border-scl-border text-xs">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => openConvert(intent)} data-testid={`tour-intent-convert-${intent.id}`} className="mt-2 h-9 px-3 rounded-full bg-scl-teal text-white text-xs font-semibold inline-flex items-center gap-1 hover:brightness-105">
                    <CalendarClock className="w-3.5 h-3.5" /> Schedule this tour
                  </button>
                )}
              </div>
            ))}
            {intents.filter((i) => i.status === "requested").length === 0 && (
              <div className="text-xs text-scl-charcoal/60 col-span-full">No open requests. New ones show up as families tap the tour CTA.</div>
            )}
          </div>
        </div>
      )}

      <form onSubmit={submit} className="bg-white rounded-2xl shadow-warm-sm p-4 mb-5 space-y-3" data-testid="tour-add-form">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-scl-teal/10 flex items-center justify-center"><CalendarClock className="w-5 h-5 text-scl-teal" /></div>
          <div>
            <div className="font-serif font-bold text-scl-charcoal text-lg">Schedule a tour</div>
            <div className="text-xs text-scl-charcoal/60">A warm follow-up email fires automatically ~1 hour after the tour time.</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input placeholder="Family name" value={form.family_name} onChange={(e) => setForm({ ...form, family_name: e.target.value })} data-testid="tour-family-name" />
          <Input type="email" placeholder="Family email" value={form.family_email} onChange={(e) => setForm({ ...form, family_email: e.target.value })} data-testid="tour-family-email" />
          <Input type="datetime-local" value={form.tour_at} onChange={(e) => setForm({ ...form, tour_at: e.target.value })} data-testid="tour-when" />
          <select
            value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
            data-testid="tour-language"
            className="h-10 rounded-md border border-scl-border bg-white px-3 text-sm"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
          <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sm:col-span-2" data-testid="tour-notes" />
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={submitting} data-testid="tour-add-submit" className="h-10 px-4 rounded-full bg-scl-coral text-white text-sm font-semibold inline-flex items-center gap-1.5 hover:brightness-105 disabled:opacity-60">
            <Plus className="w-4 h-4" /> {submitting ? "Saving…" : "Add tour"}
          </button>
        </div>
      </form>

      <div className="bg-scl-cream/60 rounded-2xl p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="tour-followup-controls">
        <div className="flex-1 min-w-[220px] text-xs text-scl-charcoal/70">
          A cron runs every 15 minutes. You can also preview or trigger the follow-up right now.
        </div>
        <button onClick={() => runFollowups(true)} disabled={running} data-testid="tour-followup-preview" className="h-9 px-3 rounded-full bg-scl-teal text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-105 disabled:opacity-60">
          <Users className="w-3.5 h-3.5" /> Preview
        </button>
        <button onClick={() => runFollowups(false)} disabled={running} data-testid="tour-followup-run" className="h-9 px-3 rounded-full bg-scl-coral text-white text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-105 disabled:opacity-60">
          <Mail className="w-3.5 h-3.5" /> Send now
        </button>
        {lastRun && (
          <span className="text-[10px] uppercase tracking-widest text-scl-charcoal/60 font-semibold" data-testid="tour-followup-last">
            Last: {lastRun.dry_run ? "preview" : "send"} · sent {lastRun.sent} · candidates {lastRun.candidates}
          </span>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-warm-sm overflow-hidden" data-testid="tours-list">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-scl-cream text-left"><tr><Th>Family</Th><Th>Email</Th><Th>Tour at</Th><Th>Lang</Th><Th>Follow-up</Th><Th></Th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-6 text-center text-scl-charcoal/60">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-scl-charcoal/60">No tours scheduled yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-scl-border/60" data-testid={`tour-row-${r.id}`}>
                  <Td className="font-medium">{r.family_name}</Td>
                  <Td><a href={`mailto:${r.family_email}`} className="text-scl-teal hover:underline">{r.family_email}</a></Td>
                  <Td className="text-xs text-scl-charcoal/80">{r.tour_at ? new Date(r.tour_at).toLocaleString() : "—"}</Td>
                  <Td className="text-xs uppercase font-semibold">{(r.language || "en")}</Td>
                  <Td>
                    {r.followup_sent
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-scl-teal"><CheckCircle2 className="w-3.5 h-3.5" /> Sent</span>
                      : <span className="text-xs text-scl-charcoal/50">Pending</span>}
                  </Td>
                  <Td className="text-right">
                    <button onClick={() => remove(r)} data-testid={`tour-delete-${r.id}`} title="Delete tour" className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-scl-brick/10 text-scl-brick">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

